import { format } from 'date-fns';
import { ACC_INITIAL_ADDRESS, APPLICANT_TO_VICTIM, DATE_FORMAT_SHORT, TIME_FORMAT, TRANSFER_TYPES } from '../../constants';
import { getCitizenship, isArmenianCountry } from '../countryHelper';

export const formatDate = (date) => date && format(date, DATE_FORMAT_SHORT);
export const formatTime = (time) => time && format(time, TIME_FORMAT);

export const bindClaimInsuredPersonData = (personalData) => ({
  firstName: personalData.firstName,
  lastName: personalData.lastName,
  passport: personalData.passportNum,
  passportGiven: formatDate(personalData.passportGivenDate),
  passportValidUntil: formatDate(personalData.passportValidUntilDate),
  passportFrom: personalData.passportFrom,
  citizenship: personalData.citizenship,
  birthday: personalData.birthday,
  socCard: personalData.socialCard || '',
});

export const bindClaimApplicantData = (personalData) => ({
  firstName: personalData.applicantFName,
  lastName: personalData.applicantLName,
  citizenship: personalData.applicantCitizenship,
  birthday: formatDate(personalData.applicantBDay),
  passport: personalData.applicantPassNum,
  passportGiven: formatDate(personalData.applicantPassGivenDate),
  passportValidUntil: formatDate(personalData.applicantPassValidUntilDate),
  passportFrom: personalData.applicantPassFrom,
  gender: personalData.applicantGender,
  socCard: personalData.applicantSocCard || '',
});

export const bindClaimPayloadData = (
  personalData,
  applicant,
  language,
) => {
  const payload = {};

  payload.applicantIs = personalData.applicantIs;

  if (payload.applicantIs === APPLICANT_TO_VICTIM[1].value) {
    payload.applicant = applicant;
  } else if (payload.applicantIs === APPLICANT_TO_VICTIM[2].value) {
    payload.applicant = {
      hvhh: personalData.applicantOrganizationHvhh,
      name: personalData[`applicantOrganizationName${language.toUpperCase()}`],
      email: personalData.applicantOrganizationEmail,
      phone: personalData.applicantOrganizationPhone,
    };
  }

  return payload;
};

export const bindPaymentReceiverData = (compensation, language) => {
  const paymentReceiver = {};

  if (compensation.paymentReceiverType === APPLICANT_TO_VICTIM[1].value) {
    paymentReceiver.firstName = compensation.paymentReceiverFirstName;
    paymentReceiver.lastName = compensation.paymentReceiverLastName;
    paymentReceiver.passport = compensation.paymentReceiverPassport;
    paymentReceiver.passportGiven = formatDate(compensation.paymentReceiverPassportGivenDate);
    paymentReceiver.passportValidUntil = formatDate(compensation.paymentReceiverPassportValidUntil);
    paymentReceiver.passportFrom = compensation.paymentReceiverPassportFrom;
    paymentReceiver.birthday = compensation.paymentReceiverBirthday;
    paymentReceiver.gender = compensation.paymentReceiverGender;
    paymentReceiver.citizenship = compensation.paymentReceiverCitizenship;
    paymentReceiver.socCard = compensation.paymentReceiverSocCard || '';
  } else if (compensation.paymentReceiverType === APPLICANT_TO_VICTIM[2].value) {
    paymentReceiver.hvhh = compensation.paymentReceiverHvhh;
    paymentReceiver.name = compensation[`paymentReceiverName${language.toUpperCase()}`];
    paymentReceiver.email = compensation.paymentReceiverEmail;
    paymentReceiver.phone = compensation.paymentReceiverPhone;
  }

  return paymentReceiver;
};

export const bindParticipantData = (payload, personalData) => {
  const participant = {};
  if (payload.participantIs === APPLICANT_TO_VICTIM[1].value) {
    participant.firstName = personalData.participantFirstName;
    participant.lastName = personalData.participantLastName;
    participant.citizenship = personalData.participantCitizenship;
    participant.birthday = formatDate(personalData.participantBDay);
    participant.passport = personalData.participantPassNum;
    participant.passportGiven = formatDate(personalData.participantPassGivenDate);
    participant.passportValidUntil = formatDate(personalData.participantPassValidUntilDate);
    participant.passportFrom = personalData.participantPassFrom;
    participant.socCard = personalData.participantSocCard || '';
  }
  return participant;
};

export const bindVictimData = (personalData) => {
  const victim = {};
  victim.firstName = personalData.firstName;
  victim.lastName = personalData.lastName;
  victim.passport = personalData.passportNum;
  victim.passportGiven = formatDate(personalData.passportGivenDate);
  victim.passportValidUntil = formatDate(personalData.passportValidUntilDate);
  victim.passportFrom = personalData.passportFrom;
  victim.citizenship = personalData.citizenship;
  victim.socCard = personalData.socialCard || '';
  victim.organizationName = personalData.organizationName;
  victim.taxIdNumber = personalData.hvhh;

  return victim;
};

/* eslint-disable no-param-reassign */
export const bindClaimTransportData = (payload, personalData, accidentInfo) => {
  payload.transportPlateNumber = personalData.stateNumber;
  payload.transportManufacturer = personalData.transportManufacturer;
  payload.transportModel = personalData.transportModel;
  payload.transportVin = personalData.vin;
  payload.transportLocationCountry = accidentInfo.carCountry;
  payload.transportLocationState = accidentInfo.carState;
  payload.transportLocationCityVillage = accidentInfo.carVillage;
  payload.transportLocationStreet = accidentInfo.carStreet;
};

export const bindClaimAttachmentsData = (attachments) => attachments.responses.map((resp) => {
  const { type, uuid } = resp;
  return { type, uuids: [].concat(uuid) };
});

export const bindClaimAccidentCarListData = (accidentInfo) => accidentInfo.carList.map((car) => {
  const { stateNumber, manufacturer, model } = car;
  return {
    plateNumber: stateNumber,
    transportManufacturer: manufacturer,
    transportModel: model,
  };
});

export const bindClaimFormProperties = (applicant, policies, countries) => ({
  bindPersonalData: () => ({
    ...applicant,
    ...getCitizenship(applicant.country, countries),
    applicant,
  }),
  bindAccidentInfo: () => ({
    policies,
  }),
  bindCompensation: () => ({
    bankAccount: applicant.accountNumber,
  }),
  bindConclusion: () => ({
    email: applicant.email,
    emailConfirmation: applicant.email,
    phone: applicant.phone,
    cellPhone: applicant.mobilePhone,
    citizenship: getCitizenship(applicant.country, countries).citizenship,
  }),
});

export const updateClaimForm = (dispatch, updateForm, applicant, policies, countries) => {
  if (applicant && policies) {
    const properties = bindClaimFormProperties(applicant, policies, countries);
    dispatch(
      updateForm({
        name: 'personalData',
        data: properties.bindPersonalData(),
      }),
    );
    dispatch(
      updateForm({
        name: 'accidentInfo',
        data: properties.bindAccidentInfo(),
      }),
    );
    dispatch(
      updateForm({
        name: 'compensation',
        data: properties.bindCompensation(),
      }),
    );
    dispatch(
      updateForm({
        name: 'conclusion',
        data: properties.bindConclusion(),
      }),
    );
  }
};

export const handleAccidentCountryChange = (
  accCountry,
  setAccAddress,
  setValue,
  setAccVillages,
) => {
  const isAccInArmenia = isArmenianCountry(accCountry);
  setAccAddress({
    ...ACC_INITIAL_ADDRESS,
    isAccInArmenia,
    accCountry,
  });
  setValue('accCountry', accCountry, true);
  setValue('isAccInArmenia', isAccInArmenia);
  setValue('accState', null);
  setValue('accVillage', null);
  if (isAccInArmenia) {
    setAccVillages([]);
  }
};

export const prepareVehicleInsuranceCheckPayload = (vehicles, date) => ({
  insuranceDate: date,
  plateNumbers: vehicles.map((v) => v.stateNumber),
});

// @TODO move these methods to corresponding folders
export const prepareCmtplPropertyPayload = (data) => {
  const {
    personalData,
    accidentInfo,
    compensation,
    conclusion,
    attachments: formAttachments,
    language,
  } = data;

  const payload = {};
  const victim = {};
  const applicant = {};

  const isApplicantPhysicalEntity = personalData.applicantType === 1;

  victim.type = isApplicantPhysicalEntity ? 'physical' : 'legal';

  victim.firstName = personalData.firstName;
  victim.lastName = personalData.lastName;
  victim.passport = personalData.passportNum;
  victim.passportGiven = formatDate(personalData.passportGivenDate);
  victim.passportValidUntil = formatDate(personalData.passportValidUntilDate);
  victim.passportFrom = personalData.passportFrom;
  victim.citizenship = personalData.citizenship;

  victim.organizationName = personalData.organizationName;
  victim.taxIdNumber = personalData.hvhh;

  payload.victim = victim;

  applicant.firstName = personalData.applicantFName;
  applicant.lastName = personalData.applicantLName;
  applicant.citizenship = personalData.applicantCitizenship;
  applicant.birthday = formatDate(personalData.applicantBDay);
  applicant.passport = personalData.applicantPassNum;
  applicant.passportGiven = formatDate(personalData.applicantPassGivenDate);
  applicant.passportValidUntil = formatDate(personalData.applicantPassValidUntilDate);
  applicant.passportFrom = personalData.applicantPassFrom;
  applicant.gender = personalData.applicantGender;

  payload.applicantIs = personalData.applicantToVictim;
  if (payload.applicantIs === 'other') {
    payload.applicant = applicant;
  } else if (payload.applicantIs === 'otherLegal') {
    payload.applicant = {
      hvhh: personalData.applicantOrganizationHvhh,
      name: personalData[`applicantOrganizationName${language.toUpperCase()}`],
      email: personalData.applicantOrganizationEmail,
      phone: personalData.applicantOrganizationPhone,
    };
  }

  payload.accidentDate = formatDate(accidentInfo.accidentDate);
  payload.accidentTime = formatTime(accidentInfo.accidentTime);
  payload.accidentCountry = accidentInfo.accCountry;
  payload.accidentState = accidentInfo.accState;
  payload.accidentCityVillage = accidentInfo.accVillage;
  payload.accidentStreet = accidentInfo.accStreet;
  payload.accidentLegalProceeding = accidentInfo.proceedingStatus;

  payload.transportPlateNumber = personalData.stateNumber;
  payload.transportManufacturer = personalData.transportManufacturer;
  payload.transportModel = personalData.transportModel;
  payload.transportVin = personalData.vin;
  payload.transportLocationCountry = accidentInfo.carCountry;
  payload.transportLocationState = accidentInfo.carState;
  payload.transportLocationCityVillage = accidentInfo.carVillage;
  payload.transportLocationStreet = accidentInfo.carStreet;

  payload.damageCost = accidentInfo.damageAmount;
  payload.damagedParts = accidentInfo.damagedParts;

  payload.compensationType = compensation.compensationType;
  payload.repairCompany = compensation.repairCompany;
  payload.transferType = compensation.transferType;
  payload.transferBank = compensation.transferBank;
  payload.transferAccount = compensation.transferType === 'account' ? compensation.transferAccount : null;

  const paymentReceiver = {};

  payload.paymentReceiverIs = compensation.paymentReceiverType;

  if (compensation.paymentReceiverType === 'other') {
    paymentReceiver.firstName = compensation.paymentReceiverFirstName;
    paymentReceiver.lastName = compensation.paymentReceiverLastName;
    paymentReceiver.passport = compensation.paymentReceiverPassport;
    paymentReceiver.passportGiven = formatDate(compensation.paymentReceiverPassportGivenDate);
    paymentReceiver.passportValidUntil = formatDate(compensation.paymentReceiverPassportValidUntil);
    paymentReceiver.passportFrom = compensation.paymentReceiverPassportFrom;
    paymentReceiver.birthday = compensation.paymentReceiverBirthday;
    paymentReceiver.gender = compensation.paymentReceiverGender;
    paymentReceiver.citizenship = compensation.paymentReceiverCitizenship;
  } else if (compensation.paymentReceiverType === 'otherLegal') {
    paymentReceiver.hvhh = compensation.paymentReceiverHvhh;
    paymentReceiver.name = compensation[`paymentReceiverName${language.toUpperCase()}`];
    paymentReceiver.email = compensation.paymentReceiverEmail;
    paymentReceiver.phone = compensation.paymentReceiverPhone;
  }

  payload.paymentReceiver = paymentReceiver;

  payload.email = conclusion.email;
  payload.emailConfirmation = conclusion.emailConfirmation;
  payload.phone = conclusion.phone || null;
  payload.cellPhone = conclusion.cellPhone || null;
  payload.receivedCompensationSum = conclusion.receivedCompensationSum;

  payload.attachments = formAttachments.responses.map((resp) => {
    const { type, uuid } = resp;
    return { type, uuids: [].concat(uuid) };
  });

  payload.language = language;

  return payload;
};

// @TODO move these methods to corresponding folders
export const prepareCmtplPersonalPayload = (data) => {
  const {
    personalData,
    accidentInfo,
    compensation,
    conclusion,
    attachments: formAttachments,
    language,
  } = data;

  const payload = {};
  const victim = {};
  const applicant = {};

  victim.firstName = personalData.firstName;
  victim.lastName = personalData.lastName;
  victim.citizenship = personalData.citizenship;
  victim.documentType = personalData.documentType.id;

  if (victim.documentType === 'passport') {
    victim.passport = personalData.documentNum;
  } else {
    victim.documentNumber = personalData.documentNum;
  }

  victim.passportGiven = formatDate(personalData.passportGiven);
  victim.passportValidUntil = formatDate(personalData.passportValidUntil);
  victim.passportFrom = personalData.passportFrom;

  payload.victim = victim;
  payload.victimRole = personalData.victimToAcc;

  applicant.firstName = personalData.applicantFName;
  applicant.lastName = personalData.applicantLName;
  applicant.citizenship = personalData.applicantCitizenship;
  applicant.birthday = formatDate(personalData.applicantBDay);
  applicant.passport = personalData.applicantPassNum;
  applicant.passportGiven = formatDate(personalData.applicantPassGivenDate);
  applicant.passportValidUntil = formatDate(personalData.applicantPassValidUntilDate);
  applicant.passportFrom = personalData.applicantPassFrom;
  applicant.gender = personalData.applicantGender;

  payload.applicantIs = personalData.applicantToVictim;
  if (payload.applicantIs === 'other') {
    payload.applicant = applicant;
  } else if (payload.applicantIs === 'otherLegal') {
    payload.applicant = {
      hvhh: personalData.applicantOrganizationHvhh,
      name: personalData[`applicantOrganizationName${language.toUpperCase()}`],
      email: personalData.applicantOrganizationEmail,
      phone: personalData.applicantOrganizationPhone,
    };
  }

  payload.accidentDate = formatDate(accidentInfo.accidentDate);
  payload.accidentTime = formatTime(accidentInfo.accidentTime);
  payload.accidentCountry = accidentInfo.accCountry;
  payload.accidentState = accidentInfo.accState;
  payload.accidentCityVillage = accidentInfo.accVillage;
  payload.accidentStreet = accidentInfo.accStreet;

  if (accidentInfo.carList) {
    payload.vehicles = accidentInfo.carList.map((car) => {
      const { stateNumber, manufacturer, model } = car;
      return {
        plateNumber: stateNumber,
        transportManufacturer: manufacturer,
        transportModel: model,
      };
    });
  }

  if (compensation.damageTypes) {
    const {
      healthRelatedDamage,
      incomeRelatedDamage,
      deathRelatedDamage,
    } = compensation.damageTypes;
    payload.healthRelatedDamage = healthRelatedDamage;
    payload.incomeRelatedDamage = incomeRelatedDamage;
    payload.deathRelatedDamage = deathRelatedDamage;
  }

  payload.transferType = compensation.transferType;
  payload.transferBank = compensation.transferBank;
  payload.transferAccount = compensation.transferType === 'account' ? compensation.transferAccount : null;

  const paymentReceiver = {};

  payload.paymentReceiverIs = compensation.paymentReceiverType;

  if (compensation.paymentReceiverType === 'other') {
    paymentReceiver.firstName = compensation.paymentReceiverFirstName;
    paymentReceiver.lastName = compensation.paymentReceiverLastName;
    paymentReceiver.passport = compensation.paymentReceiverPassport;
    paymentReceiver.passportGiven = formatDate(compensation.paymentReceiverPassportGivenDate);
    paymentReceiver.passportValidUntil = formatDate(compensation.paymentReceiverPassportValidUntil);
    paymentReceiver.passportFrom = compensation.paymentReceiverPassportFrom;
    paymentReceiver.birthday = compensation.paymentReceiverBirthday;
    paymentReceiver.gender = compensation.paymentReceiverGender;
    paymentReceiver.citizenship = compensation.paymentReceiverCitizenship;
  } else if (compensation.paymentReceiverType === 'otherLegal') {
    paymentReceiver.hvhh = compensation.paymentReceiverHvhh;
    paymentReceiver.name = compensation[`paymentReceiverName${language.toUpperCase()}`];
    paymentReceiver.email = compensation.paymentReceiverEmail;
    paymentReceiver.phone = compensation.paymentReceiverPhone;
  }

  payload.paymentReceiver = paymentReceiver;

  payload.email = conclusion.email;
  payload.emailConfirmation = conclusion.emailConfirmation;
  payload.phone = conclusion.phone || null;
  payload.cellPhone = conclusion.cellPhone || null;
  payload.receivedCompensationSum = conclusion.receivedCompensationSum;

  payload.attachments = formAttachments.responses.map((resp) => {
    const { type, uuid } = resp;
    return { type, uuids: [].concat(uuid) };
  });

  payload.language = language;

  return payload;
};

export const prepareTravelPayload = (data) => {
  const {
    personalData,
    accidentInfo,
    compensation,
    conclusion,
    attachments: formAttachments,
    language,
  } = data;

  const insuredPerson = bindClaimInsuredPersonData(personalData);
  const applicant = bindClaimApplicantData(personalData);
  const payload = bindClaimPayloadData(personalData, applicant, language);

  payload.insuredPerson = insuredPerson;

  payload.accidentDate = formatDate(accidentInfo.accidentDate);
  payload.contractNumber = accidentInfo.contract.policyNumber;
  payload.insuredOrganizationName = accidentInfo.contract.insurer;
  payload.damageCost = accidentInfo.damageAmount;
  payload.currency = accidentInfo.currency;
  payload.accidentDetails = accidentInfo.accidentInformation ?? '';
  payload.accidentCountry = accidentInfo.accCountry;
  payload.accidentState = accidentInfo.accState;
  payload.accidentCityVillage = accidentInfo.accVillage;
  payload.accidentStreet = accidentInfo.accStreet;
  payload.afterSeeingDoctorDate = accidentInfo.afterSeeingDoctor;
  payload.beforeSeeingDoctorDate = accidentInfo.beforeSeeingDoctor;

  payload.transferType = compensation.transferType;
  payload.transferBank = compensation.transferBank;
  payload.transferAccount = compensation.transferType === 'account' ? compensation.transferAccount : null;

  const paymentReceiver = bindPaymentReceiverData(compensation, language);

  payload.paymentReceiverIs = compensation.paymentReceiverType;
  payload.paymentReceiver = paymentReceiver;

  payload.email = conclusion.email;
  payload.emailConfirmation = conclusion.emailConfirmation;
  payload.phone = conclusion.phone || null;
  payload.cellPhone = conclusion.cellPhone || null;

  payload.attachments = bindClaimAttachmentsData(formAttachments);

  payload.language = language;

  return payload;
};

export const preparePropertyPayload = (data) => {
  const {
    personalData,
    accidentInfo,
    compensation,
    conclusion,
    attachments: formAttachments,
    language,
  } = data;

  const applicant = bindClaimApplicantData(personalData);
  const payload = bindClaimPayloadData(personalData, applicant, language);

  const propertyOwner = {};

  const isOwnerPhysicalEntity = personalData.applicantType === 1;

  propertyOwner.type = isOwnerPhysicalEntity ? 'physical' : 'legal';

  let address = '';

  if (isOwnerPhysicalEntity) {
    propertyOwner.citizenship = personalData.citizenship;
    propertyOwner.firstName = personalData[`firstName${language.toUpperCase()}`];
    propertyOwner.lastName = personalData[`lastName${language.toUpperCase()}`];
    propertyOwner.passport = personalData.passport;
    propertyOwner.passportGiven = personalData.passportGiven;
    propertyOwner.passportValidUntil = personalData.passportValidUntil;
    propertyOwner.passportFrom = personalData.passportFrom;
    address = personalData[`physAddress${language.toUpperCase()}`];
  } else {
    propertyOwner.taxIdNumber = personalData.hvhh;
    propertyOwner.organizationName = personalData[`organizationName${language.toUpperCase()}`];
    address = personalData[`legalAddress${language.toUpperCase()}`];
  }

  payload.propertyOwner = propertyOwner;
  payload.propertyOwnerAddress = address;

  payload.accidentDate = formatDate(accidentInfo.accidentDate);
  payload.accidentTime = accidentInfo.accidentTime;
  payload.damageCost = accidentInfo.damageAmount;

  payload.accidentCountry = accidentInfo.accCountry;
  payload.accidentState = accidentInfo.accState;
  payload.accidentCityVillage = accidentInfo.accCityVillage;
  payload.accidentStreet = accidentInfo.accStreet;

  payload.loanAgreementExists = accidentInfo.loanAgreementExists;
  payload.loanCompanyName = accidentInfo.loanCompanyName;
  payload.damageCauses = accidentInfo.damageCauses;
  payload.damageCauseOther = accidentInfo.damageCauseOther;
  payload.accidentDiscovererIs = accidentInfo.accidentDiscovererIs;
  payload.accidentDiscovererOther = accidentInfo.accidentDiscovererOther;
  payload.organizations = accidentInfo.organizations;
  payload.organizationOther = accidentInfo.organizationOther;
  payload.accidentDetails = accidentInfo.accidentDetails ?? '';
  payload.accidentPrevention = accidentInfo.accidentPrevention;
  payload.damagedProperties = accidentInfo.damagedProperties.map((dp) => ({
    name: dp.propertyName,
    yearProduced: dp.propertyYear,
    price: dp.propertyPrice,
    description: dp.damageDescription,
  }));

  payload.transferType = compensation.transferType;
  payload.transferBank = compensation.transferBank;
  payload.transferAccount = compensation.transferType === 'account' ? compensation.transferAccount : null;

  const paymentReceiver = bindPaymentReceiverData(compensation, language);

  payload.paymentReceiverIs = compensation.paymentReceiverType;
  payload.paymentReceiver = paymentReceiver;

  payload.email = conclusion.email;
  payload.emailConfirmation = conclusion.emailConfirmation;
  payload.phone = conclusion.phone || null;
  payload.cellPhone = conclusion.cellPhone || null;

  payload.attachments = bindClaimAttachmentsData(formAttachments);

  payload.language = language;
  payload.policyNumber = personalData.policyNumber;
  payload.insuredProperties = personalData.selectedProperties.map((id) => ({
    masysId: id,
  }));

  return payload;
};

export const prepareAccidentPayload = (data) => {
  const {
    personalData,
    accidentInfo,
    compensation,
    conclusion,
    attachments: formAttachments,
    language,
  } = data;

  const insuredPerson = bindClaimInsuredPersonData(personalData);
  const applicant = bindClaimApplicantData(personalData);
  const payload = bindClaimPayloadData(personalData, applicant, language);

  payload.insuredPerson = insuredPerson;

  payload.accidentDate = formatDate(accidentInfo.accidentDate);
  payload.contractNumber = accidentInfo.contract.policyNumber;
  payload.insuredOrganizationName = accidentInfo.contract.insurer;
  payload.damageCost = accidentInfo.damageAmount;
  payload.currency = accidentInfo.currency;
  payload.accidentDetails = accidentInfo.accidentInformation ?? '';
  payload.accidentTime = accidentInfo.accidentTime;
  payload.accidentCountry = accidentInfo.accCountry;
  payload.accidentState = accidentInfo.accState;
  payload.accidentCityVillage = accidentInfo.accVillage;
  payload.accidentStreet = accidentInfo.accStreet;

  payload.transferType = compensation.transferType;
  payload.transferBank = compensation.transferBank;
  payload.transferAccount = (
    compensation.transferType === TRANSFER_TYPES[1].value
  ) ? compensation.transferAccount : null;

  const paymentReceiver = bindPaymentReceiverData(compensation, language);

  payload.paymentReceiverIs = compensation.paymentReceiverType;
  payload.paymentReceiver = paymentReceiver;

  payload.email = conclusion.email;
  payload.emailConfirmation = conclusion.emailConfirmation;
  payload.phone = conclusion.phone || null;
  payload.cellPhone = conclusion.cellPhone || null;

  payload.attachments = bindClaimAttachmentsData(formAttachments);

  payload.language = language;

  return payload;
};
